import type {ForeignKey} from './types'
import type {Suggestion} from '@/components/ui/completion-editor'

interface TableRef {
    table: string
    alias: string
}

const SQL_KEYWORDS = new Set([
    'select', 'from', 'where', 'join', 'inner', 'left', 'right', 'outer', 'cross',
    'on', 'and', 'or', 'not', 'in', 'is', 'null', 'like', 'between', 'exists',
    'order', 'group', 'having', 'limit', 'offset', 'union', 'insert', 'update',
    'delete', 'set', 'values', 'into', 'create', 'alter', 'drop', 'table',
])

/**
 * Parses FROM clause and preceding JOINs to find table references.
 * Excludes the current joined table.
 */
export function parseFromTables(textBefore: string, excludeTable: string): TableRef[] {
    const tables: TableRef[] = []

    const fromMatch = textBefore.match(/\bFROM\s+(\w+)(?:\s+AS\s+(\w+)|\s+(\w+))?/i)
    if (fromMatch) {
        const table = fromMatch[1].toLowerCase()
        const rawAlias = (fromMatch[2] ?? fromMatch[3])?.toLowerCase()
        const alias = rawAlias && !SQL_KEYWORDS.has(rawAlias) ? rawAlias : table
        if (table !== excludeTable.toLowerCase()) {
            tables.push({table, alias})
        }
    }

    const joinRegex = /\bJOIN\s+(\w+)(?:\s+AS\s+(\w+)|\s+(\w+))?/gi
    let match
    while ((match = joinRegex.exec(textBefore)) !== null) {
        const table = match[1].toLowerCase()
        const rawAlias = (match[2] ?? match[3])?.toLowerCase()
        const alias = rawAlias && !SQL_KEYWORDS.has(rawAlias) ? rawAlias : table
        if (table !== excludeTable.toLowerCase() && !tables.some(t => t.table === table && t.alias === alias)) {
            tables.push({table, alias})
        }
    }

    return tables
}

/**
 * Builds FK-based completion items for a JOIN ON clause.
 */
export function buildFkCompletions(
    foreignKeys: ForeignKey[],
    joinedTable: string,
    joinedAlias: string,
    fromTables: TableRef[],
) {
    const options: {label: string; type: string; boost: number}[] = []

    for (const fk of foreignKeys) {
        const fkFrom = fk.fromTable.toLowerCase()
        const fkTo = fk.toTable.toLowerCase()
        const joined = joinedTable.toLowerCase()

        for (const ref of fromTables) {
            if (fkFrom === joined && fkTo === ref.table) {
                options.push({
                    label: `${joinedAlias}.${fk.fromColumn} = ${ref.alias}.${fk.toColumn}`,
                    type: 'text',
                    boost: 10,
                })
            }
            if (fkFrom === ref.table && fkTo === joined) {
                options.push({
                    label: `${ref.alias}.${fk.fromColumn} = ${joinedAlias}.${fk.toColumn}`,
                    type: 'text',
                    boost: 10,
                })
            }
        }
    }

    return options
}

/**
 * Parses all tables in the query (FROM + JOINs), without excluding any.
 */
export function parseAllTables(textBefore: string): string[] {
    const tables: string[] = []

    const fromMatch = textBefore.match(/\bFROM\s+(\w+)/i)
    if (fromMatch) {
        tables.push(fromMatch[1].toLowerCase())
    }

    const joinRegex = /\bJOIN\s+(\w+)/gi
    let match
    while ((match = joinRegex.exec(textBefore)) !== null) {
        const table = match[1].toLowerCase()
        if (!tables.includes(table)) {
            tables.push(table)
        }
    }

    return tables
}

/**
 * Builds table name suggestions for JOIN based on FK relationships with existing tables.
 */
export function buildFkTableSuggestions(
    foreignKeys: ForeignKey[],
    existingTables: string[],
    partial: string,
) {
    const suggestions = new Set<string>()

    for (const fk of foreignKeys) {
        const fkFrom = fk.fromTable.toLowerCase()
        const fkTo = fk.toTable.toLowerCase()

        for (const existing of existingTables) {
            if (fkTo === existing && !existingTables.includes(fkFrom)) {
                suggestions.add(fkFrom)
            }
            if (fkFrom === existing && !existingTables.includes(fkTo)) {
                suggestions.add(fkTo)
            }
        }
    }

    return [...suggestions]
        .filter(t => t.startsWith(partial.toLowerCase()))
        .map(table => ({
            label: table,
            type: 'type' as const,
            boost: 50,
        }))
}

/**
 * Matches JOIN <table> ON <partial> and builds FK-based ON condition suggestions.
 */
export function matchFkJoinOn(textBefore: string, foreignKeys: ForeignKey[]): Suggestion[] {
    const joinOnMatch = textBefore.match(
        /\bJOIN\s+(\w+)(?:\s+(?:AS\s+)?(\w+))?\s+ON\s+(\S*)$/i,
    )
    if (!joinOnMatch) return []

    const joinedTable = joinOnMatch[1].toLowerCase()
    const joinedAlias = joinOnMatch[2]?.toLowerCase() ?? joinedTable
    const fromTables = parseFromTables(textBefore, joinedTable)
    if (fromTables.length === 0) return []

    const options = buildFkCompletions(foreignKeys, joinedTable, joinedAlias, fromTables)
    return options.map(o => ({value: o.label, description: 'FK join'}))  
}

/**
 * Matches JOIN <partial> and builds FK-related table name suggestions.
 */
export function matchFkJoinTable(textBefore: string, foreignKeys: ForeignKey[]): Suggestion[] {
    const joinMatch = textBefore.match(/\bJOIN\s+(\w*)$/i)
    if (!joinMatch) return []

    const partial = joinMatch[1] ?? ''
    const existingTables = parseAllTables(textBefore)
    if (existingTables.length === 0) return []

    const options = buildFkTableSuggestions(foreignKeys, existingTables, partial)
    return options.map(o => ({value: o.label, description: 'FK table'}))  
}
